import { useState } from "react";

import ULThemeLink from "@/components/ULThemeLink";
import {
  getAdminApiBaseUrl,
  requestPasswordReset,
} from "@/utils/api/requestPasswordReset";

interface ForgotPasswordLinkProps {
  linkText: string | undefined;
  getEmail: () => string;
  clientId?: string;
  fallbackHref?: string;
  sentText?: string;
  missingEmailText?: string;
}

type State = "idle" | "pending" | "sent" | "missing-email";

function ForgotPasswordLink({
  linkText,
  getEmail,
  clientId,
  fallbackHref,
  sentText = "If that account exists, we've sent a reset link to your inbox.",
  missingEmailText = "Enter your email above first, then click here.",
}: ForgotPasswordLinkProps) {
  const [state, setState] = useState<State>("idle");
  const adminBase = getAdminApiBaseUrl();

  // Dev / mock environments: fall back to default Auth0 link so
  // ul-context-inspector and legacy flows keep working.
  if (!adminBase) {
    if (!fallbackHref) return null;
    return <ULThemeLink href={fallbackHref}>{linkText}</ULThemeLink>;
  }

  const onClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (state === "pending" || state === "sent") return;

    const email = getEmail().trim();
    if (!email) {
      setState("missing-email");
      return;
    }

    setState("pending");
    await requestPasswordReset({ email, clientId });
    setState("sent");
  };

  if (state === "sent") {
    return (
      <p className="text-(length:--ul-theme-font-body-text-size) text-muted-foreground">
        {sentText}
      </p>
    );
  }

  return (
    <>
      <ULThemeLink href="#" onClick={onClick} disabled={state === "pending"}>
        {linkText}
      </ULThemeLink>
      {/*
        `w-full` is load-bearing and is NOT about width.

        This component returns a FRAGMENT, so the link and this message are
        siblings in whatever the caller's container happens to be. Two of the
        three screens wrap it in a plain block div, where they stack and this
        class changes nothing — a <p> is already full width there. GuestMaker's
        brand CSS makes that row `display: flex`, and there the message became a
        second flex ITEM: measured live, it sat 0px from the link ("Reset
        passwordEnter your email above first…") and shoved the link 272px left
        out of its right-aligned position.

        `w-full` + the row's `flex-wrap: wrap` puts it back on its own line. Kept
        here rather than only in the brand CSS so the next brand that reaches for
        `display: flex` on this row does not reintroduce it.
      */}
      {state === "missing-email" && (
        <p className="mt-1 w-full text-(length:--ul-theme-font-body-text-size) text-muted-foreground">
          {missingEmailText}
        </p>
      )}
    </>
  );
}

export default ForgotPasswordLink;
